import { useAuth } from "@/_core/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { trpc } from "@/lib/trpc";
import { CheckCircle2, Copy, Key, AlertTriangle } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

const TTL_OPTIONS = [
  { label: "1 hour", value: "3600" },
  { label: "6 hours", value: "21600" },
  { label: "1 day", value: "86400" },
  { label: "7 days", value: "604800" },
  { label: "30 days", value: "2592000" },
];

export default function RenewToken() {
  const { user, loading: authLoading } = useAuth({ redirectOnUnauthenticated: true });

  const params = new URLSearchParams(window.location.search);
  const tokenId = params.get("token_id") || "";
  const tokenType = (params.get("type") || "secret") as "secret" | "proxy" | "env";

  const [ttlSeconds, setTtlSeconds] = useState("3600");
  const [newToken, setNewToken] = useState<string | null>(null);
  const [resourceName, setResourceName] = useState<string | null>(null);

  const renewMutation = trpc.tokens.renew.useMutation({
    onSuccess: (data) => {
      setNewToken(data.token);
      setResourceName(data.resourceName);
      toast.success("New token generated");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleRenew = () => {
    renewMutation.mutate({
      tokenId,
      type: tokenType,
      ttlSeconds: parseInt(ttlSeconds),
    });
  };

  const copyToClipboard = () => {
    if (!newToken) return;
    navigator.clipboard.writeText(newToken);
    toast.success("Token copied to clipboard");
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!tokenId) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3 text-destructive">
              <AlertTriangle className="h-5 w-5" />
              <p>Invalid renewal link. No token ID provided.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-muted/30">
      <Card className="max-w-lg w-full">
        <CardHeader className="text-center">
          <div className="mx-auto p-3 bg-primary/10 rounded-full w-fit mb-2">
            <Key className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Renew Access Token</CardTitle>
          <CardDescription>
            Your {tokenType === "env" ? "virtual env" : tokenType} token has expired.
            Generate a new one below.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!newToken ? (
            <>
              <div className="space-y-2">
                <label className="text-sm font-medium">Token Lifetime</label>
                <Select value={ttlSeconds} onValueChange={setTtlSeconds}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TTL_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                className="w-full"
                onClick={handleRenew}
                disabled={renewMutation.isPending}
              >
                {renewMutation.isPending ? (
                  <>
                    <Spinner className="mr-2 h-4 w-4" />
                    Generating...
                  </>
                ) : (
                  "Generate New Token"
                )}
              </Button>
            </>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center gap-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                <span className="text-sm font-medium text-green-700 dark:text-green-400">
                  New token generated for {resourceName || "your resource"}
                </span>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Your New Token</label>
                <div className="relative">
                  <code className="block w-full p-3 pr-12 bg-muted rounded-lg text-xs break-all font-mono select-all">
                    {newToken}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-1.5 right-1.5"
                    onClick={copyToClipboard}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Copy this token and paste it into your CLI or script. This token will not be shown again.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
